import { TrendingUp, VisibilityOff } from '@mui/icons-material'
import { Alert, Chip, Link, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import type { ControllerDescription } from '@/features/controller/types'
import { ControllerBulkAction } from '@/features/controller/types'
import { makeGrafanaLinkForControllersDashboard } from '@/features/controller/utils/grafanaLinks'

interface ControllerBulkActionSelectorProps {
  bulkAction: ControllerBulkAction | null
  updateBulkAction: (value: ControllerBulkAction | null) => void
  selectedControllers: ControllerDescription[]
}

export function ControllerBulkActionSelector({
  bulkAction,
  updateBulkAction,
  selectedControllers,
}: Readonly<ControllerBulkActionSelectorProps>) {
  const { t } = useTranslation()

  if (selectedControllers.length === 0) {
    return <Alert severity="info">{t('controllers.bulk_actions.no_selection_info')}</Alert>
  }

  const bulkActions: SelectFieldOption<ControllerBulkAction>[] = [
    {
      id: 'replace_configuration',
      value: ControllerBulkAction.replace_configuration,
      label: t('controllers.bulk_actions.replace_configuration.label'),
    },
  ]

  const grafanaUrl = makeGrafanaLinkForControllersDashboard(null, selectedControllers)

  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <CustomTypography>{t('controllers.bulk_actions.select')}</CustomTypography>
      <CustomSelectField
        label={t('controllers.bulk_actions.select_placeholder')}
        options={bulkActions}
        sx={{ width: 250 }}
        value={bulkAction || ''}
        onChange={(event) => updateBulkAction(event.target.value as ControllerBulkAction)}
      />
      <CustomIconButton Icon={VisibilityOff} disabled={bulkAction === null} onClick={() => updateBulkAction(null)} />
      {/* None of the selected controllers has an equipment number, so there is nothing to filter the
          dashboard by and the link would show every controller instead of the chosen ones. */}
      {grafanaUrl !== null && (
        <Link href={grafanaUrl} rel="noreferrer" target="_blank">
          <Chip
            color="default"
            icon={<TrendingUp />}
            label={t('controllers.observability.grafana')}
            variant="outlined"
          />
        </Link>
      )}
    </Stack>
  )
}
